/**
 * Button primitive — design.md § Components: Buttons.
 *
 * Variants: primary (ink fill, bg text), secondary (transparent, ink text, line-2 border),
 * ghost (transparent, ink text, no border), danger (error fill, bg text).
 * Sizes: sm, md, lg.
 * Uses transition-base (200ms ease-out) for hover/focus states.
 */
import type { ButtonHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: [
    'bg-ink text-bg',
    'hover:bg-ink-2 hover:-translate-y-px',
    'focus-visible:ring-accent-l',
  ].join(' '),
  secondary: [
    'bg-transparent text-ink border border-line-2',
    'hover:bg-bg-warm',
    'focus-visible:ring-accent-l',
  ].join(' '),
  ghost: [
    'bg-transparent text-ink',
    'hover:bg-bg-warm',
    'focus-visible:ring-accent-l',
  ].join(' '),
  danger: [
    'bg-error text-bg',
    'hover:bg-error/90',
    'focus-visible:ring-accent-l',
  ].join(' '),
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-small',
  md: 'px-[18px] py-2.5 text-small',
  lg: 'px-6 py-3 text-body',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      className,
      disabled,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center',
          'rounded-md font-sans font-medium',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'transition-base',
          'disabled:cursor-not-allowed disabled:opacity-60',
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);
